import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import adminOpsService from '../../services/adminOpsService';
import {
  LayoutDashboard,
  Brain,
  BookOpen,
  Terminal,
  FileEdit,
  Workflow,
  Library,
  FileType2,
  ClipboardList,
  FlaskConical,
  ArrowRight,
  RefreshCw,
  AlertTriangle,
  Server,
  Lock,
  CheckCircle,
  Database,
  Globe,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Skeleton } from '../../components/ui/skeleton';
import { toast } from 'sonner';

const StatCard = ({ icon: Icon, label, value, subtext, color, onClick }) => (
  <div 
    className={`bg-stone-900 border border-stone-800 rounded-xl p-5 cursor-pointer hover:border-amber-500/30 transition-all ${onClick ? 'hover:scale-[1.01]' : ''}`}
    onClick={onClick}
    data-testid={`stat-${label.toLowerCase().replace(/\s+/g, '-')}`}
  >
    <div className="flex items-start justify-between">
      <div>
        <p className="text-sm text-slate-500 mb-1">{label}</p>
        <p className={`text-3xl font-bold ${color}`}>{value}</p>
        {subtext && <p className="text-xs text-slate-500 mt-1">{subtext}</p>}
      </div>
      <div className={`p-3 rounded-xl bg-opacity-10 ${color.replace('text-', 'bg-').replace('-400', '-500/20')}`}>
        <Icon className={color} size={24} />
      </div>
    </div>
  </div>
);

const QuickActionCard = ({ icon: Icon, title, description, to, color }) => {
  const navigate = useNavigate();
  return (
    <div 
      className="bg-stone-900 border border-stone-800 rounded-xl p-5 cursor-pointer hover:border-amber-500/30 transition-all group"
      onClick={() => navigate(to)}
      data-testid={`action-${title.toLowerCase().replace(/\s+/g, '-')}`}
    >
      <div className="flex items-center gap-4">
        <div className={`p-3 rounded-xl ${color}`}>
          <Icon className="text-white" size={20} />
        </div>
        <div className="flex-1">
          <h3 className="font-semibold text-white group-hover:text-amber-400 transition-colors">
            {title}
          </h3>
          <p className="text-sm text-slate-500">{description}</p>
        </div>
        <ArrowRight className="text-slate-600 group-hover:text-amber-400 transition-colors" size={20} />
      </div>
    </div>
  );
};

const AdminOverviewPage = () => {
  const { user, getAuthHeaders } = useAuth();
  const navigate = useNavigate();
  const [overview, setOverview] = useState(null);
  const [systemStatus, setSystemStatus] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchData = async () => {
    setLoading(true);
    try {
      adminOpsService.setAuthHeaders(getAuthHeaders());
      const [overviewData, statusData] = await Promise.all([
        adminOpsService.getOverview(),
        adminOpsService.getSystemStatus(),
      ]);
      setOverview(overviewData);
      setSystemStatus(statusData);
    } catch (error) {
      toast.error('Failed to load admin overview');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const counts = overview?.counts || {};

  return (
    <div className="space-y-6" data-testid="admin-overview-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-3">
            <LayoutDashboard className="text-amber-400" size={28} />
            Admin Overview
          </h1>
          <p className="text-slate-500 mt-1">Internal AI operations and training tools</p>
        </div>
        <Button variant="outline" onClick={fetchData} disabled={loading}>
          <RefreshCw size={16} className={`mr-2 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* Security Notice */}
      <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-4">
        <div className="flex items-start gap-3">
          <AlertTriangle className="text-amber-400 flex-shrink-0 mt-0.5" size={20} />
          <div>
            <h3 className="font-semibold text-amber-300">Admin-Only Access</h3>
            <p className="text-sm text-slate-400">
              This control center is restricted to authorized personnel. All actions are logged.
              Normal users cannot access or see these pages.
            </p>
          </div>
        </div>
      </div>

      {/* System Status Banner */}
      {loading ? (
        <Skeleton className="h-16 bg-stone-800" />
      ) : (
        <div className={`rounded-xl p-4 flex items-center justify-between ${
          systemStatus?.overall_status === 'healthy' 
            ? 'bg-emerald-500/10 border border-emerald-500/30' 
            : 'bg-amber-500/10 border border-amber-500/30'
        }`}>
          <div className="flex items-center gap-3">
            {systemStatus?.overall_status === 'healthy' ? (
              <CheckCircle className="text-emerald-400" size={24} />
            ) : (
              <AlertTriangle className="text-amber-400" size={24} />
            )}
            <div>
              <p className="font-semibold text-white capitalize">
                System Status: {systemStatus?.overall_status || 'Unknown'}
              </p>
              <p className="text-sm text-slate-400">
                {systemStatus?.components?.length || 0} components monitored
              </p>
            </div>
          </div>
          <Button variant="outline" size="sm" onClick={() => navigate('/admin/system-status')}>
            <Server size={16} className="mr-2" />
            View Details
          </Button>
        </div>
      )}

      {/* Stats Grid */}
      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {[...Array(8)].map((_, i) => <Skeleton key={i} className="h-28 bg-stone-800" />)}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard
            icon={ClipboardList}
            label="Pending Reviews"
            value={counts.pending_reviews || 0}
            subtext="Items awaiting review"
            color="text-amber-400"
            onClick={() => navigate('/admin/review-queue')}
          />
          <StatCard
            icon={BookOpen}
            label="Knowledge Entries"
            value={counts.knowledge_entries || 0}
            subtext="Active entries"
            color="text-emerald-400"
            onClick={() => navigate('/admin/knowledge-base')}
          />
          <StatCard
            icon={Terminal}
            label="Live Prompts"
            value={counts.prompt_versions || 0}
            subtext="Active versions"
            color="text-amber-400"
            onClick={() => navigate('/admin/prompts')}
          />
          <StatCard
            icon={FileEdit}
            label="Letter Materials"
            value={counts.letter_materials || 0}
            subtext="Training examples"
            color="text-emerald-400"
            onClick={() => navigate('/admin/letter-training')}
          />
          <StatCard
            icon={Workflow}
            label="Workflow Rules"
            value={counts.workflow_rules || 0}
            subtext="Active rules"
            color="text-teal-400"
            onClick={() => navigate('/admin/workflow-rules')}
          />
          <StatCard
            icon={Library}
            label="Active Methods"
            value={counts.active_methods || 0}
            subtext="Processing methods"
            color="text-teal-400"
            onClick={() => navigate('/admin/methods')}
          />
          <StatCard
            icon={FlaskConical}
            label="Sandbox Tests"
            value={counts.sandbox_tests || 0}
            subtext="Test runs"
            color="text-pink-400"
            onClick={() => navigate('/admin/sandbox')}
          />
          <StatCard
            icon={Lock}
            label="Permissions"
            value={user?.role === 'platform_owner' ? 'Manage' : 'View'}
            subtext="Access control"
            color="text-orange-400"
            onClick={() => navigate('/admin/permissions')}
          />
        </div>
      )}

      {/* Quick Actions */}
      <div>
        <h2 className="text-lg font-semibold text-white mb-4">Quick Actions</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <QuickActionCard
            icon={Globe}
            title="QA Website Access"
            description="Manage websites available to Authorized QA Proxy sessions."
            to="/admin/qa-website-access"
            color="bg-sky-600"
          />
          <QuickActionCard
            icon={Database}
            title="DataImpulse Location Catalog"
            description="Upload and manage the shared State, City, and ZIP catalog"
            to="/admin/data-providers/dataimpulse-location-catalog"
            color="bg-emerald-600"
          />
          <QuickActionCard
            icon={Brain}
            title="Parser Training"
            description="Review extracted items, manage classification rules"
            to="/admin/parser-training"
            color="bg-amber-600"
          />
          <QuickActionCard
            icon={Terminal}
            title="Prompt Management"
            description="Version and manage AI prompts"
            to="/admin/prompts"
            color="bg-emerald-600"
          />
          <QuickActionCard
            icon={Workflow}
            title="Rules & Methods"
            description="Qualification, routing, packaging, and generation governance"
            to="/admin/rules-methods"
            color="bg-teal-600"
          />
          <QuickActionCard
            icon={Library}
            title="Method Library"
            description="Define processing methods for disputes"
            to="/admin/methods"
            color="bg-emerald-600"
          />
          <QuickActionCard
            icon={FileType2}
            title="Template Editor"
            description="Manage letter templates and content"
            to="/admin/template-editor"
            color="bg-teal-600"
          />
          <QuickActionCard
            icon={ClipboardList}
            title="Review Queue"
            description="Review and validate AI outputs"
            to="/admin/review-queue"
            color="bg-amber-600"
          />
          <QuickActionCard
            icon={FlaskConical}
            title="Testing"
            description="Test rules, routing, and template rendering"
            to="/admin/testing"
            color="bg-pink-600"
          />
          <QuickActionCard
            icon={BookOpen}
            title="AI Knowledge Base"
            description="Manage transcripts, SOPs, and training materials"
            to="/admin/knowledge-base"
            color="bg-amber-600"
          />
        </div>
      </div>

      {/* Governance Summary */}
      <div>
        <h2 className="text-lg font-semibold text-white mb-4">Governance Summary</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <div className="bg-stone-900 border border-stone-800 rounded-xl p-5">
            <div className="flex items-center gap-3 mb-3">
              <div className="p-2 rounded-lg bg-emerald-500/20">
                <CheckCircle className="text-emerald-400" size={20} />
              </div>
              <h3 className="font-semibold text-white">Global Output Policy</h3>
            </div>
            <p className="text-sm text-slate-400 mb-3">Flat/Plain legal output enforced for all factual letters.</p>
            <div className="flex flex-wrap gap-2">
              <span className="px-2 py-1 text-xs rounded-full bg-emerald-500/20 text-emerald-400 border border-emerald-500/30">Shapes: OFF</span>
              <span className="px-2 py-1 text-xs rounded-full bg-emerald-500/20 text-emerald-400 border border-emerald-500/30">Colors: OFF</span>
              <span className="px-2 py-1 text-xs rounded-full bg-emerald-500/20 text-emerald-400 border border-emerald-500/30">Cards: OFF</span>
            </div>
          </div>

          <div className="bg-stone-900 border border-stone-800 rounded-xl p-5">
            <div className="flex items-center gap-3 mb-3">
              <div className="p-2 rounded-lg bg-amber-500/20">
                <Lock className="text-amber-400" size={20} />
              </div>
              <h3 className="font-semibold text-white">Selection Safety</h3>
            </div>
            <p className="text-sm text-slate-400 mb-3">User selection is pool only. Admin rules decide final grouping.</p>
            <div className="flex flex-wrap gap-2">
              <span className="px-2 py-1 text-xs rounded-full bg-amber-500/20 text-amber-400 border border-amber-500/30">Auto-validate</span>
              <span className="px-2 py-1 text-xs rounded-full bg-amber-500/20 text-amber-400 border border-amber-500/30">No override</span>
            </div>
          </div>

          <div className="bg-stone-900 border border-stone-800 rounded-xl p-5">
            <div className="flex items-center gap-3 mb-3">
              <div className="p-2 rounded-lg bg-purple-500/20">
                <LayoutDashboard className="text-purple-400" size={20} />
              </div>
              <h3 className="font-semibold text-white">Package Splitting</h3>
            </div>
            <p className="text-sm text-slate-400 mb-3">Auto-split by method. Mismatched items cannot merge.</p>
            <div className="flex flex-wrap gap-2">
              <span className="px-2 py-1 text-xs rounded-full bg-purple-500/20 text-purple-400 border border-purple-500/30">Method-based</span>
              <span className="px-2 py-1 text-xs rounded-full bg-purple-500/20 text-purple-400 border border-purple-500/30">Bureau-split</span>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="bg-stone-900 border border-stone-800 rounded-xl p-4 text-center">
        <p className="text-xs text-slate-500">
          All admin actions are logged. Training materials, prompts, and configurations are not exposed to end users.
          Last updated: {overview?.last_updated ? new Date(overview.last_updated).toLocaleString() : 'N/A'}
        </p>
      </div>
    </div>
  );
};

export default AdminOverviewPage;
